import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

type PageState = 'loading' | 'not-found' | 'not-ready';

/**
 * Handles short URL redirects from /m/:code to /memorial/:uuid
 * Looks up the medallion by short_code, then redirects if linked to a memorial.
 */
const ShortUrlRedirect = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<PageState>('loading');

  useEffect(() => {
    const lookupAndRedirect = async () => {
      if (!code) {
        setPageState('not-found');
        return;
      }

      try {
        const { data, error: dbError } = await supabase
          .from('medallions')
          .select('memorial_id')
          .eq('short_code', code.toUpperCase())
          .single();

        if (dbError || !data) {
          setPageState('not-found');
          return;
        }

        if (data.memorial_id) {
          navigate(`/memorial/${data.memorial_id}`, { replace: true });
        } else {
          setPageState('not-ready');
        }
      } catch (err) {
        console.error('Error looking up short code:', err);
        setPageState('not-found');
      }
    };

    lookupAndRedirect();
  }, [code, navigate]);

  if (pageState === 'not-found') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-semibold text-foreground">Invalid Code</h1>
          <p className="text-muted-foreground">
            This QR code is not recognized. It may be damaged or invalid.
          </p>
          <a
            href="/"
            className="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90 transition-opacity"
          >
            Go Home
          </a>
        </div>
      </div>
    );
  }

  if (pageState === 'not-ready') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
        <div className="text-center space-y-4 max-w-md">
          <p className="text-sm font-medium tracking-wide text-muted-foreground uppercase">
            Pontis Life
          </p>
          <h1 className="text-2xl font-semibold text-foreground">
            This memorial hasn't been set up yet
          </h1>
          <p className="text-muted-foreground">
            The family hasn't finished setting up this memorial page. Please check back later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
};

export default ShortUrlRedirect;
